import React, { useState, useEffect } from 'react';
import { assignmentsApi } from '../api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { Plus, CheckCircle2, Circle, Trash2, Pencil } from 'lucide-react';
import { format } from 'date-fns';

const Assignments = () => {
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [assignmentToDelete, setAssignmentToDelete] = useState(null);
  const [editingAssignment, setEditingAssignment] = useState(null);
  const [newAssignment, setNewAssignment] = useState({
    title: '',
    description: '',
    subject: '',
    due_date: ''
  });

  useEffect(() => {
    fetchAssignments();
  }, []);

  const fetchAssignments = async () => {
    try {
      const response = await assignmentsApi.getAll();
      setAssignments(response.data.sort((a, b) => new Date(a.due_date) - new Date(b.due_date)));
    } catch (error) {
      toast.error('Failed to load assignments');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    try {
      await assignmentsApi.create(newAssignment);
      toast.success('Assignment added');
      setShowAdd(false);
      setNewAssignment({ title: '', description: '', subject: '', due_date: '' });
      fetchAssignments();
    } catch (error) {
      toast.error('Failed to add assignment');
    }
  };

  const handleToggleComplete = async (assignment) => {
    try {
      await assignmentsApi.update(assignment.id, { completed: !assignment.completed });
      toast.success(assignment.completed ? 'Assignment marked as incomplete' : 'Assignment completed!');
      fetchAssignments();
    } catch (error) {
      toast.error('Failed to update assignment');
    }
  };

  const openDeleteConfirm = (assignment) => {
    setAssignmentToDelete(assignment);
    setShowDeleteConfirm(true);
  };

  const handleDelete = async () => {
    if (!assignmentToDelete) return;
    
    const id = assignmentToDelete.id;
    setShowDeleteConfirm(false);
    
    try {
      await assignmentsApi.delete(id);
      toast.success('Assignment deleted');
      fetchAssignments();
    } catch (error) {
      toast.error('Failed to delete assignment');
    } finally {
      setAssignmentToDelete(null);
    }
  };

  const openEditDialog = (assignment) => {
    setEditingAssignment({
      ...assignment,
      due_date: assignment.due_date.split('T')[0] // Format for date input
    });
    setShowEdit(true);
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    try {
      await assignmentsApi.update(editingAssignment.id, {
        title: editingAssignment.title,
        description: editingAssignment.description,
        subject: editingAssignment.subject,
        due_date: editingAssignment.due_date,
        completed: editingAssignment.completed
      });
      toast.success('Assignment updated');
      setShowEdit(false);
      setEditingAssignment(null);
      fetchAssignments();
    } catch (error) {
      toast.error('Failed to update assignment');
    }
  };

  const pendingAssignments = assignments.filter(a => !a.completed);
  const completedAssignments = assignments.filter(a => a.completed);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg text-muted-foreground">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-5xl mx-auto p-6 md:p-12 space-y-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl md:text-5xl font-bold mb-2">Assignments</h1>
            <p className="text-lg text-muted-foreground">
              {pendingAssignments.length} pending • {completedAssignments.length} completed
            </p>
          </div>
          <Dialog open={showAdd} onOpenChange={setShowAdd}>
            <DialogTrigger asChild>
              <Button className="rounded-full" data-testid="add-assignment-button">
                <Plus className="w-4 h-4 mr-2" />
                Add Assignment
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="add-assignment-dialog">
              <DialogHeader>
                <DialogTitle>Add New Assignment</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleAdd} className="space-y-4">
                <div className="space-y-2">
                  <Label>Title</Label>
                  <Input
                    value={newAssignment.title}
                    onChange={(e) => setNewAssignment({ ...newAssignment, title: e.target.value })}
                    placeholder="Assignment title"
                    required
                    data-testid="assignment-title-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Subject</Label>
                  <Input
                    value={newAssignment.subject}
                    onChange={(e) => setNewAssignment({ ...newAssignment, subject: e.target.value })}
                    placeholder="e.g. Mathematics"
                    required
                    data-testid="assignment-subject-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Textarea
                    value={newAssignment.description}
                    onChange={(e) => setNewAssignment({ ...newAssignment, description: e.target.value })}
                    placeholder="Assignment details..."
                    rows={3}
                    data-testid="assignment-description-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Due Date</Label>
                  <Input
                    type="date"
                    value={newAssignment.due_date}
                    onChange={(e) => setNewAssignment({ ...newAssignment, due_date: e.target.value })}
                    required
                    data-testid="assignment-due-date-input"
                  />
                </div>
                <Button type="submit" className="w-full rounded-full" data-testid="submit-assignment">
                  Add Assignment
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="space-y-8">
          <div>
            <h2 className="text-2xl font-semibold mb-4 flex items-center gap-2">
              <Circle className="w-5 h-5 text-warning" />
              Pending
            </h2>
            {pendingAssignments.length > 0 ? (
              <div className="space-y-4">
                {pendingAssignments.map(assignment => (
                  <Card
                    key={assignment.id}
                    className="shadow-sm border-border/50 hover:shadow-md transition-shadow"
                    data-testid={`assignment-${assignment.id}`}
                  >
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4">
                        <button
                          onClick={() => handleToggleComplete(assignment)}
                          className="mt-1"
                          data-testid={`toggle-assignment-${assignment.id}`}
                        >
                          <Circle className="w-6 h-6 text-muted-foreground hover:text-primary transition-colors" />
                        </button>
                        <div className="flex-1">
                          <h3 className="text-xl font-semibold mb-1">{assignment.title}</h3>
                          <p className="text-sm text-primary font-medium mb-2">{assignment.subject}</p>
                          {assignment.description && (
                            <p className="text-muted-foreground mb-3">{assignment.description}</p>
                          )}
                          <p className="text-sm text-warning font-medium">
                            Due: {format(new Date(assignment.due_date), 'EEEE, MMMM d, yyyy')}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openEditDialog(assignment)}
                            className="text-primary hover:text-primary hover:bg-primary/10"
                            data-testid={`edit-assignment-${assignment.id}`}
                          >
                            <Pencil className="w-5 h-5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openDeleteConfirm(assignment)}
                            className="text-destructive hover:text-destructive hover:bg-destructive/10"
                            data-testid={`delete-assignment-${assignment.id}`}
                          >
                            <Trash2 className="w-5 h-5" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="shadow-sm">
                <CardContent className="p-12 text-center">
                  <CheckCircle2 className="w-16 h-16 mx-auto text-success mb-4" />
                  <p className="text-lg text-muted-foreground">No pending assignments</p>
                </CardContent>
              </Card>
            )}
          </div>

          {completedAssignments.length > 0 && (
            <div>
              <h2 className="text-2xl font-semibold mb-4 flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-success" />
                Completed
              </h2>
              <div className="space-y-4">
                {completedAssignments.map(assignment => (
                  <Card
                    key={assignment.id}
                    className="shadow-sm border-border/50 opacity-60"
                    data-testid={`completed-assignment-${assignment.id}`}
                  >
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4">
                        <button
                          onClick={() => handleToggleComplete(assignment)}
                          className="mt-1"
                          data-testid={`toggle-completed-${assignment.id}`}
                        >
                          <CheckCircle2 className="w-6 h-6 text-success" />
                        </button>
                        <div className="flex-1">
                          <h3 className="text-xl font-semibold mb-1 line-through">{assignment.title}</h3>
                          <p className="text-sm text-muted-foreground">{assignment.subject}</p>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openEditDialog(assignment)}
                            className="text-primary hover:text-primary hover:bg-primary/10"
                            data-testid={`edit-completed-${assignment.id}`}
                          >
                            <Pencil className="w-5 h-5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openDeleteConfirm(assignment)}
                            className="text-destructive hover:text-destructive hover:bg-destructive/10"
                            data-testid={`delete-completed-${assignment.id}`}
                          >
                            <Trash2 className="w-5 h-5" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Edit Assignment Dialog */}
        <Dialog open={showEdit} onOpenChange={setShowEdit}>
          <DialogContent data-testid="edit-assignment-dialog">
            <DialogHeader>
              <DialogTitle>Edit Assignment</DialogTitle>
            </DialogHeader>
            {editingAssignment && (
              <form onSubmit={handleEdit} className="space-y-4">
                <div className="space-y-2">
                  <Label>Title</Label>
                  <Input
                    value={editingAssignment.title}
                    onChange={(e) => setEditingAssignment({ ...editingAssignment, title: e.target.value })}
                    placeholder="Assignment title"
                    required
                    data-testid="edit-assignment-title-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Subject</Label>
                  <Input
                    value={editingAssignment.subject}
                    onChange={(e) => setEditingAssignment({ ...editingAssignment, subject: e.target.value })}
                    placeholder="e.g. Mathematics"
                    required
                    data-testid="edit-assignment-subject-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Textarea
                    value={editingAssignment.description || ''}
                    onChange={(e) => setEditingAssignment({ ...editingAssignment, description: e.target.value })}
                    placeholder="Assignment details..."
                    rows={3}
                    data-testid="edit-assignment-description-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Due Date</Label>
                  <Input
                    type="date"
                    value={editingAssignment.due_date}
                    onChange={(e) => setEditingAssignment({ ...editingAssignment, due_date: e.target.value })}
                    required
                    data-testid="edit-assignment-due-date-input"
                  />
                </div>
                <Button type="submit" className="w-full rounded-full" data-testid="submit-edit-assignment">
                  Save Changes
                </Button>
              </form>
            )}
          </DialogContent>
        </Dialog>
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent data-testid="delete-assignment-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Delete Assignment</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-muted-foreground">
              Are you sure you want to delete this assignment?
            </p>
            {assignmentToDelete && (
              <div className="p-3 bg-secondary/30 rounded-lg border border-border">
                <p className="font-semibold text-base mb-1">{assignmentToDelete.title}</p>
                <p className="text-sm text-muted-foreground">
                  {assignmentToDelete.subject} • Due: {format(new Date(assignmentToDelete.due_date), 'MMM d, yyyy')}
                </p>
              </div>
            )}
            <div className="flex gap-2 justify-end">
              <Button
                variant="outline"
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setAssignmentToDelete(null);
                }}
                data-testid="cancel-delete-assignment-button"
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleDelete}
                data-testid="confirm-delete-assignment-button"
              >
                Delete
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Assignments;
